import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Library, Loader2, Trophy, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchTournamentDecks, fetchMetaSummary } from '../lib/metaApi';

const PAGE_SIZE = 24;

const PLACEMENTS = [
  ['', 'Any placement'],
  ['winners', 'Event winners'],
  ['top4', 'Top 4'],
  ['top8', 'Top 8'],
];

const SORTS = [
  ['recent', 'Most recent'],
  ['best', 'Best finish'],
  ['played', 'Most piloted'],
];

function ordinal(n) {
  if (n === null || n === undefined) return '—';
  const rules = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
  const suffix = rules[new Intl.PluralRules('en', { type: 'ordinal' }).select(n)] || 'th';
  return `${n}${suffix}`;
}

function DeckTile({ deck, onOpen, t }) {
  const best = deck.bestResult || {};
  return (
    <Card
      className="cursor-pointer transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-ring"
      onClick={() => onOpen(deck.code)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold leading-tight">{deck.archetype}</h3>
          {deck.bestRank === 1 && (
            <Badge className="shrink-0 gap-1">
              <Trophy className="h-3 w-3" />
              {t('deckDatabase.won') || 'Won'}
            </Badge>
          )}
        </div>
        {deck.name && (
          <p className="text-sm text-muted-foreground line-clamp-1 mb-3" title={deck.name}>
            {deck.name}
          </p>
        )}
        <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">{t('deckDatabase.bestFinish') || 'Best finish'}</dt>
          <dd className="text-right font-medium tabular-nums">
            {ordinal(deck.bestRank)}{best.field ? ` / ${best.field}` : ''}
          </dd>
          <dt className="text-muted-foreground">{t('deckDatabase.piloted') || 'Piloted'}</dt>
          <dd className="text-right tabular-nums">
            {deck.entries}× {deck.events > 1 && `(${deck.events} events)`}
          </dd>
          <dt className="text-muted-foreground">{t('deckDatabase.date') || 'Date'}</dt>
          <dd className="text-right tabular-nums">{best.endDateText || '—'}</dd>
        </dl>
        {best.eventName && (
          <p className="mt-3 pt-3 border-t text-xs text-muted-foreground line-clamp-1"
            title={best.eventName}>
            {best.eventName}
            {best.prefecture && ` · ${best.prefecture}`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function TournamentDecks() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [result, setResult] = useState(null);
  const [archetypeOptions, setArchetypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const archetype = searchParams.get('archetype') || '';
  const placement = searchParams.get('placement') || '';
  const sort = searchParams.get('sort') || 'recent';
  const card = searchParams.get('card') || '';
  const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1);

  const [cardInput, setCardInput] = useState(card);
  useEffect(() => { setCardInput(card); }, [card]);

  // Filters live in the URL so a filtered view is linkable — the meta page's
  // "Decks" buttons deep-link straight into this list.
  const setParam = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value) next.set(key, value); else next.delete(key);
      }
      if (!('page' in updates)) next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchTournamentDecks({
          archetype, placement, sort, card, page, pageSize: PAGE_SIZE,
        });
        if (!cancelled) { setResult(data); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [archetype, placement, sort, card, page]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const summary = await fetchMetaSummary();
        if (!cancelled) {
          setArchetypeOptions(summary.archetypes.filter(a => a.label !== 'Other').map(a => a.label));
        }
      } catch {
        /* the archetype dropdown is a convenience; the list works without it */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalPages = useMemo(
    () => (result ? Math.max(Math.ceil(result.total / PAGE_SIZE), 1) : 1),
    [result]
  );

  const hasFilters = Boolean(archetype || placement || card || sort !== 'recent');

  return (
    <div className="min-h-[calc(100vh-48px-40px)] bg-gradient-to-b from-background to-muted/30">
      <Helmet>
        <title>Tournament Deck Database | Ultraman Card Game Tools</title>
        <meta
          name="description"
          content="Browse real Ultraman Card Game tournament decklists by archetype, placement and card, and load any of them into the deck builder."
        />
      </Helmet>

      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Library className="h-8 w-8" />
            <h1 className="text-3xl font-bold">
              {t('deckDatabase.title') || 'Tournament Deck Database'}
            </h1>
          </div>
          <p className="text-white/80 max-w-2xl">
            {t('deckDatabase.subtitle') ||
              'Every published decklist from tracked tournaments. Filter by archetype, finish or a card you want to see played, then load any list straight into the deck builder.'}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 mb-6">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterArchetype') || 'Archetype'}
            </span>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[200px]"
              value={archetype}
              onChange={(e) => setParam({ archetype: e.target.value })}
            >
              <option value="">{t('deckDatabase.allArchetypes') || 'All archetypes'}</option>
              {archetypeOptions.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterPlacement') || 'Placement'}
            </span>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={placement}
              onChange={(e) => setParam({ placement: e.target.value })}
            >
              {PLACEMENTS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterSort') || 'Sort'}
            </span>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={sort}
              onChange={(e) => setParam({ sort: e.target.value })}
            >
              {SORTS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <form
            className="flex flex-col gap-1"
            onSubmit={(e) => { e.preventDefault(); setParam({ card: cardInput.trim() }); }}
          >
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterCard') || 'Playing card'}
            </span>
            <div className="flex gap-1">
              <Input
                className="h-9 w-[160px]"
                placeholder="BP01-007"
                value={cardInput}
                onChange={(e) => setCardInput(e.target.value)}
              />
              <Button type="submit" size="sm" variant="secondary" className="h-9">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </form>

          {hasFilters && (
            <Button variant="ghost" size="sm" className="h-9"
              onClick={() => setSearchParams(new URLSearchParams())}>
              <X className="h-4 w-4 mr-1" />
              {t('deckDatabase.clear') || 'Clear'}
            </Button>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : !result?.decks?.length ? (
          <div className="py-16 text-center">
            <Library className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">
              {t('deckDatabase.noResults') || 'No decklists match these filters.'}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {result.total.toLocaleString()} {t('deckDatabase.decksFound') || 'decklists'}
              {archetype && <> · {archetype}</>}
              {card && <> · {t('deckDatabase.playing') || 'playing'} {card}</>}
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {result.decks.map((deck) => (
                <DeckTile key={deck.code} deck={deck} t={t}
                  onOpen={(code) => navigate(`/decks/tournament/${code}`)} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setParam({ page: String(page - 1) })}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground tabular-nums px-2">
                  {page} / {totalPages}
                </span>
                <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setParam({ page: String(page + 1) })}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
